import {useQuery} from "@tanstack/react-query"
import moment from "moment"
import {useCallback, useEffect, useMemo, useState} from "react"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import ButtonCheckbox from "components/buttons/buttonCheckbox"
import {generalSettingsService, reportsService} from "services"
import {GeneralSettingField} from "types/settings/general-setting"
import {YesNo} from "types/common"
import {handleError} from "helpers"

type GradeExportRow = Record<string, any> & {
    id: number
    campusId?: number
    isChecked?: boolean
    isCheckboxDisabled?: boolean
}

const DEFAULT_PAGE_SIZE = 20

export function PracticalClinicExportFameTable() {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [data, setData] = useState<GradeExportRow[]>([])
    const [total, setTotal] = useState(0)

    const {data: fameAdvantageEnabled} = useQuery(
        ["fame-advantage-setting"],
        async () => {
            const result = await generalSettingsService.getSettings([GeneralSettingField.FameAdvantageEnabled])
            return result[0]?.value === YesNo.Yes
        },
        {initialData: false}
    )

    const fields = useMemo(() => {
        let fields = [
            "Date",
            "Student Id",
            "Student Name",
            "Program Name",
            "Course Id",
            "Course Name",
            "Submodule",
            "Mapping Code",
            "Grade"
        ]

        if (fameAdvantageEnabled) {
            fields = fields.concat(["External ID", "Deleted", "Kiss ID"])
        }

        fields = fields.concat(["Exported", "Date Export", "Time Export", "SIS ID", "Sync Error"])
        return fields
    }, [fameAdvantageEnabled])

    const columns = useMemo(() => {
        let columns: any[] = [
            {title: "Date", field: "date"},
            {title: "Student Id", field: "studentId"},
            {title: "Student Name", field: "studentName"},
            {title: "Program Name", field: "programName"},
            {title: "Course Id", field: "courseCode"},
            {title: "Course Name", field: "courseName"},
            {title: "Submodule", field: "submoduleTitle"},
            {title: "Mapping Code", field: "mappingCode"},
            {
                title: "Grade",
                field: "gradeText",
                style: {minWidth: 70},
                headerStyle: {minWidth: "70px"}
            }
        ]

        if (fameAdvantageEnabled) {
            columns = columns.concat([
                {title: "External ID", field: "externalId"},
                {title: "Deleted", field: "deleted"},
                {title: "Kiss ID", field: "externalCampusId"}
            ])
        }

        columns = columns.concat([
            {
                title: "Exported",
                field: "exported",
                style: {minWidth: 90},
                headerStyle: {minWidth: "90px"}
            },
            {title: "Date Export", field: "dateExport"},
            {title: "Time Export", field: "timeExport"},
            {title: "SIS ID", field: "sisId"},
            {
                title: "Sync Error",
                field: "syncError",
                style: {color: "var(--klassapp-red)", maxWidth: 200, wordWrap: "break-word"},
                headerStyle: {maxWidth: "200px"}
            }
        ])

        return columns
    }, [fameAdvantageEnabled])

    const renderExportedCheckbox = useCallback((id: any, isChecked: boolean) => {
        return (
            <div style={{width: "100%", display: "flex", pointerEvents: "none"}}>
                <ButtonCheckbox id={String(id)} name="all" onClick={() => {}} allChecked={isChecked} />
            </div>
        )
    }, [])

    const {data: gradeExportRes, isFetching} = useQuery(
        ["homepage-grade-export", page, pageSize],
        async () => {
            const {gradeExport = [], total = 0} = await reportsService.listGradeExport({
                filter: {
                    missingFameId: true
                },
                range: {page, pageSize}
            })
            return {gradeExport, total}
        },
        {
            onError: (err) => handleError(err),
            initialData: {gradeExport: [], total: 0}
        }
    )

    useEffect(() => {
        const gradeExport = gradeExportRes?.gradeExport ?? []
        const total = gradeExportRes?.total ?? 0

        const tableData: GradeExportRow[] = (gradeExport || []).map((item) => ({
            ...item,
            gradeText: typeof item.grade === "number" ? `${item.grade}` : "",
            date: item.date ? moment(item.date).format("MM/DD/YYYY") : "",
            exported: renderExportedCheckbox(item.id, !!item.dateExported),
            dateExport: item.dateExported ? moment(item.dateExported).format("MM/DD/YYYY") : "",
            timeExport: item.dateExported ? moment(item.dateExported).format("HH:mm") : "",
            isChecked: false,
            isCheckboxDisabled: true
        }))

        setData(tableData)
        setTotal(total)
    }, [gradeExportRes, renderExportedCheckbox])

    const onChangePage = useCallback((newPage: number) => setPage(newPage), [])

    const onChangeRowPerPage = useCallback((newPageSize: number) => {
        setPage(1)
        setPageSize(newPageSize)
    }, [])

    const onUpdateTableData = useCallback((newData: GradeExportRow[]) => {
        setData(newData)
    }, [])

    const onUpdateRowData = useCallback((updatedData: GradeExportRow) => {
        setData((prevData) => {
            const isExistChecked =
                prevData.some((item) => item.isChecked && item.id !== updatedData.id) || updatedData.isChecked

            return prevData.map((item) => {
                if (item.id === updatedData.id) {
                    return {
                        ...item,
                        isChecked: updatedData.isChecked
                    }
                }
                if (!isExistChecked) {
                    return {
                        ...item,
                        isCheckboxDisabled: false
                    }
                }
                if (item.campusId !== updatedData.campusId && !item.isCheckboxDisabled) {
                    return {
                        ...item,
                        isCheckboxDisabled: true
                    }
                }
                return item
            })
        })
    }, [])

    return (
        <>
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                isShowAction={false}
                actions={[]}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
            />
            <KlassappTable
                columns={columns}
                menuActions={[]}
                data={data}
                isLoading={isFetching}
                isShowCheckedColumn
                isShowCheckedColumnHeader={false}
                fields={fields}
                allFields={fields}
                onClickRowItem={() => {}}
                onChangeFields={() => {}}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onChangeAllFields={() => {}}
            />
        </>
    )
}
